import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useConfig } from "../contexts/ConfigContext";
import { useSelectionActions, useSelectionSelector } from "../store/selection";
import { usePopupActions, usePopupSelector } from "../store/popupStore";

/**
 * useSelectionURLSync
 *
 * Bidirectional sync between the URL and selection/popup store state.
 *
 * URL params managed:
 *   entity_id  → selectedEntityId
 *   endpoint   → selectedEndpoint
 *   popup      → isOpen + popupType  ("geo" = geo-entity popup open)
 *
 * This is the single place that handles:
 *   - Deep linking   (?entity_id=X&endpoint=Y&popup=geo → open popup on load)
 *   - Browser back/forward (URL changes → store updates)
 *   - Shareable URLs (store changes → URL updates)
 */
export function useSelectionURLSync() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const syncLockRef = useRef(false);
  const config = useConfig();

  // Endpoints from config
  const allEndpoints = config.apiEndpoints ?? [];

  // Selection store
  const { selectedEntityId, selectedEndpoint } = useSelectionSelector();
  const { setSelectedEntityId, setSelectedEndpoint } = useSelectionActions();

  // Popup store
  const { isOpen, popupType, isFullViewerOpen } = usePopupSelector();
  const { openGeoPopup, openMediaViewer, openNonGeoPopup, closePopup, openFullMediaViewer, closeFullMediaViewer } = usePopupActions();

  // ─── Direction A: URL → Store ─────────────────────────────────────────────
  // Handles both initial page load AND browser back/forward navigation.
  useEffect(() => {
    if (syncLockRef.current) return;

    const entityId = searchParams.get("entity_id");
    const endpointName = searchParams.get("endpoint");
    const popupParam = searchParams.get("popup"); // "geo" | null

    // ── Default endpoint fallback (Deep Linking / Bookmarks) ──────────────
    // When the user lands on /map without an `endpoint` query param
    // (e.g. via bookmark, typed URL, or external link), set the first
    // configured endpoint so the sidebar doesn't render empty.
    // Falls back to media (first config endpoint) if no CM Table endpoints loaded yet.
    // NOTE: intentionally excludes "/" — the homepage shows the startup modal and the
    // sidebar is hidden there. Setting a default on "/" causes the SideBar's
    // useInfiniteQuery to fire immediately, producing wasted API calls on every page load.
    if (pathname === "/map" && !endpointName && allEndpoints.length > 0) {
      syncLockRef.current = true;
      // Default to first config endpoint (usually media) when no ?endpoint= in URL
      setSelectedEndpoint(config.apiEndpoints?.[0] ?? allEndpoints[0]);
      Promise.resolve().then(() => {
        syncLockRef.current = false;
      });
      return;
    }

    // ── Mapping URL 'popup' param to popupStore types ──
    // "media-full" maps to "media-viewer" popup type + isFullViewerOpen=true
    const popupMap: Record<string, any> = {
      geo: "geo-entity",
      entity: "non-geo-entity",
      media: "media-viewer",
      "media-full": "media-viewer"
    };

    const needsEntityUpdate = entityId && entityId !== String(selectedEntityId);
    const needsEndpointUpdate = endpointName && endpointName !== selectedEndpoint?.tableName;
    const needsEntityClear = !entityId && selectedEntityId !== null;
    const needsEndpointClear = !endpointName && selectedEndpoint !== null;

    // Determine if popup state needs to change
    const targetPopupType = popupParam ? popupMap[popupParam] : null;
    const needsPopupUpdate = targetPopupType && (targetPopupType !== popupType || !isOpen);
    const needsPopupClose = !popupParam && isOpen;

    // Sync isFullViewerOpen: true only when popup=media-full
    const needsFullViewerOpen = popupParam === "media-full" && !isFullViewerOpen;
    const needsFullViewerClose = popupParam !== "media-full" && isFullViewerOpen;

    if (!needsEntityUpdate && !needsEndpointUpdate && !needsEntityClear && !needsEndpointClear && !needsPopupUpdate && !needsPopupClose && !needsFullViewerOpen && !needsFullViewerClose)
      return;

    syncLockRef.current = true;

    if (needsEntityUpdate) {
      setSelectedEntityId(Number(entityId) || entityId);
    }

    if (needsEntityClear) {
      setSelectedEntityId(null);
    }

    if (needsEndpointUpdate) {
      // Search config endpoints first, then CM Table endpoints
      const foundEndpoint = allEndpoints.find((ep) => ep.tableName === endpointName) ?? null;
      if (foundEndpoint) setSelectedEndpoint(foundEndpoint);
    }

    if (needsEndpointClear) {
      setSelectedEndpoint(null);
    }

    if (needsPopupUpdate) {
      if (targetPopupType === "geo-entity") openGeoPopup();
      if (targetPopupType === "non-geo-entity") openNonGeoPopup();
      if (targetPopupType === "media-viewer") openMediaViewer();
    }

    if (needsPopupClose) {
      closePopup();
    }

    if (needsFullViewerOpen) openFullMediaViewer();
    if (needsFullViewerClose) closeFullMediaViewer();

    Promise.resolve().then(() => {
      syncLockRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, config.apiEndpoints, pathname]);

  // ─── Direction B: Store → URL ─────────────────────────────────────────────
  useEffect(() => {
    if (syncLockRef.current) return;

    syncLockRef.current = true;
    const params = new URLSearchParams(searchParams);

    // Sync entity selection
    if (selectedEntityId) {
      params.set("entity_id", String(selectedEntityId));
    } else {
      params.delete("entity_id");
    }

    if (selectedEndpoint) {
      params.set("endpoint", selectedEndpoint.tableName);
    } else {
      params.delete("endpoint");
    }

    // Sync popup state
    if (isOpen && popupType) {
      if (popupType === "media-viewer" && isFullViewerOpen) {
        params.set("popup", "media-full");
      } else {
        const reverseMap: Record<string, string> = {
          "geo-entity": "geo",
          "non-geo-entity": "entity",
          "media-viewer": "media"
        };
        params.set("popup", reverseMap[popupType] || "geo");
      }
    } else {
      params.delete("popup");
    }

    setSearchParams(params, { replace: false });

    Promise.resolve().then(() => {
      syncLockRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityId, selectedEndpoint, isOpen, popupType, isFullViewerOpen]);
}
